(() => {
  if (customElements.get('maytrix-depth-scroll')) return;
  const clamp = (x, a = 0, b = 1) => Math.max(a, Math.min(b, x));
  const smooth = x => { x = clamp(x); return x * x * (3 - 2 * x); };
  // Keep the original approved stages, easing the change of speed at each boundary.
  const frameAt = p => {
    if (p < .06) return 0;
    if (p < .15) return 9 * smooth((p - .06) / .09);
    if (p < .43) return 9 + 55 * smooth((p - .15) / .28);
    if (p < .55) return 64;
    if (p < .82) return 64 - 55 * smooth((p - .55) / .27);
    if (p < .91) return 9 - 9 * smooth((p - .82) / .09);
    return 65 + 14 * smooth((p - .91) / .09);
  };
  class DepthScroll extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.canvas = this.querySelector('canvas');
      this.ctx = this.canvas?.getContext('2d');
      if (!this.ctx) return;
      this.frames = JSON.parse(this.querySelector('[data-depth-frames]').textContent).frames;
      this.track = this.querySelector('.myx-inside__track');
      this.sticky = this.querySelector('.myx-inside__sticky');
      this.captions = [...this.querySelectorAll('[data-phase]')];
      this.progressBar = this.querySelector('.myx-inside__progress span');
      this.mobile = matchMedia('(max-width:749px)').matches;
      this.size = this.mobile ? 600 : 960;
      this.canvas.width = this.canvas.height = this.size;
      this.cacheLimit = this.mobile ? 24 : 30;
      this.images = new Map();
      this.blobs = new Map();
      this.pending = new Set();
      this.decodePending = new Set();
      this.failed = new Set();
      this.queue = [];
      this.decodeQueue = [];
      this.active = 0;
      this.decoding = 0;
      this.progress = this.target = 0;
      this.desired = 0;
      this.lastWindow = -1;
      this.lastTime = 0;
      this.setAttribute('data-enhanced', '');
      this.onScroll = () => { if (this.visible) this.wake(); };
      this.onResize = () => { this.measure(); this.wake(); };
      window.addEventListener('scroll', this.onScroll, { passive: true, signal: this.controller.signal });
      window.addEventListener('resize', this.onResize, { passive: true, signal: this.controller.signal });
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) this.wake();
      }, { signal: this.controller.signal });
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.track);
      this.preloadObserver = new IntersectionObserver(entries => {
        if (!entries.some(e => e.isIntersecting)) return;
        this.prefetch = true;
        this.measure();
        this.desired = frameAt(this.target);
        this.prepare(this.desired);
        this.preloadObserver.disconnect();
      }, { rootMargin: '1200px' });
      this.preloadObserver.observe(this);
      this.visibleObserver = new IntersectionObserver(entries => {
        this.visible = entries.some(e => e.isIntersecting);
        if (this.visible) { this.lastTime = 0; this.wake(); }
      }, { rootMargin: '100px' });
      this.visibleObserver.observe(this);
    }
    measure() {
      const box = this.track.getBoundingClientRect();
      this.target = clamp(-box.top / Math.max(1, this.track.offsetHeight - this.sticky.offsetHeight));
    }
    wake() {
      if (!this.isConnected || document.hidden || this.raf) return;
      this.raf = requestAnimationFrame(time => this.tick(time));
    }
    tick(time) {
      this.raf = 0;
      if (!this.visible || document.hidden) return;
      this.measure();
      const dt = this.lastTime ? Math.min(50, time - this.lastTime) : 16.7;
      this.lastTime = time;
      const delta = this.target - this.progress;
      this.progress = Math.abs(delta) < .00005 ? this.target : this.progress + delta * (1 - Math.exp(-dt / 85));
      this.desired = frameAt(this.progress);
      this.prepare(this.desired);
      this.paint(this.desired);
      this.updateCopy(this.progress);
      this.dataset.progress = this.progress.toFixed(4);
      this.progressBar.style.transform = `scaleX(${this.progress})`;
      if (Math.abs(this.target - this.progress) >= .00005) this.wake();
    }
    prepare(value) {
      if (!this.prefetch) return;
      const at = Math.round(value);
      if (at !== this.lastWindow) {
        this.lastWindow = at;
        // Decode around the requested frame before drawing reaches it, in both directions.
        const near = [];
        for (let distance = 0; distance <= 10; distance++) {
          for (const i of distance ? [at + distance, at - distance] : [at]) {
            if (i >= 0 && i < this.frames.length) near.push(i);
          }
        }
        const wanted = new Set(near);
        this.decodeQueue = this.decodeQueue.filter(i => wanted.has(i));
        this.queue = [...new Set([...near, ...Array.from({ length: this.frames.length }, (_, i) => i)])]
          .filter(i => !this.blobs.has(i) && !this.pending.has(i) && !this.failed.has(i));
        for (const i of near) this.queueDecode(i);
      }
      this.pump();
    }
    pump() {
      const max = this.mobile ? 4 : 6;
      while (this.active < max && this.queue.length && this.isConnected) {
        const i = this.queue.shift();
        if (this.blobs.has(i) || this.pending.has(i) || this.failed.has(i)) continue;
        this.pending.add(i); this.active++;
        fetch(this.frames[i][this.mobile ? 'm' : 'd'], { signal: this.controller.signal, cache: 'force-cache' })
          .then(r => { if (!r.ok) throw new Error('Frame unavailable'); return r.blob(); })
          .then(blob => {
            this.blobs.set(i, blob);
            if (Math.abs(i - this.lastWindow) <= 10) this.queueDecode(i);
          })
          .catch(error => { if (error.name !== 'AbortError') this.failed.add(i); })
          .finally(() => {
            this.pending.delete(i); this.active--; this.pump();
            if (this.failed.size === this.frames.length) this.removeAttribute('data-enhanced');
          });
      }
    }
    queueDecode(i) {
      if (!this.blobs.has(i) || this.images.has(i) || this.decodePending.has(i) || this.failed.has(i)) return;
      if (!this.decodeQueue.includes(i)) this.decodeQueue.push(i);
      this.decodeQueue.sort((a, b) => Math.abs(a - this.desired) - Math.abs(b - this.desired));
      this.decodeNext();
    }
    decodeNext() {
      while (this.decoding < 2 && this.decodeQueue.length && this.isConnected) {
        const i = this.decodeQueue.shift();
        if (this.images.has(i) || this.decodePending.has(i)) continue;
        this.decoding++; this.decodePending.add(i);
        const decode = async () => {
          let bitmap;
          if ('createImageBitmap' in window) bitmap = await createImageBitmap(this.blobs.get(i));
          else {
            const url = URL.createObjectURL(this.blobs.get(i));
            try { bitmap = new Image(); bitmap.src = url; await bitmap.decode(); }
            finally { URL.revokeObjectURL(url); }
          }
          if (!this.isConnected) { bitmap.close?.(); return; }
          this.images.set(i, bitmap);
          this.evict();
          this.wake();
        };
        decode().catch(() => this.failed.add(i)).finally(() => {
          this.decoding--; this.decodePending.delete(i); this.decodeNext();
        });
      }
    }
    evict() {
      while (this.images.size > this.cacheLimit) {
        const candidates = [...this.images.keys()].filter(i => Math.abs(i - this.desired) > 3);
        if (!candidates.length) break;
        const far = candidates.reduce((a, b) => Math.abs(a - this.desired) > Math.abs(b - this.desired) ? a : b);
        this.images.get(far).close?.(); this.images.delete(far);
      }
    }
    paint(value) {
      const lo = Math.floor(value), hi = Math.ceil(value), mix = value - lo;
      let a = this.images.get(lo), b = this.images.get(hi);
      if (!a && !b) return; // Keep the previous image until a nearby frame is ready.
      if (!a) a = b;
      if (!b) b = a;
      const signature = `${lo}:${hi}:${mix.toFixed(3)}:${this.images.has(lo)}:${this.images.has(hi)}`;
      if (signature === this.signature) return;
      this.signature = signature;
      this.ctx.clearRect(0, 0, this.size, this.size);
      this.ctx.globalCompositeOperation = 'source-over';
      this.ctx.globalAlpha = 1 - mix;
      this.ctx.drawImage(a, 0, 0, this.size, this.size);
      this.ctx.globalCompositeOperation = 'lighter';
      this.ctx.globalAlpha = mix;
      this.ctx.drawImage(b, 0, 0, this.size, this.size);
      this.ctx.globalAlpha = 1;
      this.ctx.globalCompositeOperation = 'source-over';
      this.setAttribute('data-painted', '');
      this.dataset.frame = value.toFixed(2);
      this.dataset.cache = String(this.images.size);
    }
    updateCopy(p) {
      const phase = p < .15 ? 0 : p < .4 ? 1 : p < .72 ? 2 : 3;
      if (phase !== this.phase) {
        this.phase = phase;
        for (const caption of this.captions) {
          const active = Number(caption.dataset.phase) === phase;
          caption.toggleAttribute('data-active', active);
          caption.setAttribute('aria-hidden', String(!active));
        }
      }
      this.toggleAttribute('data-exploded', p >= .4 && p <= .6);
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.preloadObserver?.disconnect(); this.visibleObserver?.disconnect(); this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.raf);
      this.images?.forEach(image => image.close?.());
      this.images?.clear(); this.blobs?.clear();
      this.queue = []; this.decodeQueue = []; this.started = false;
    }
  }
  customElements.define('maytrix-depth-scroll', DepthScroll);
})();
